import { IScopeOverview } from "@/api";
import { LegalDisclaimer } from "@/components/frontend/LegalDisclaimer";
import { Box, Skeleton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useMemo } from "react";
import { DocumentSummaryHeader } from "./DocumentSummaryHeader";
import { TopicCard } from "./TopicCard";

interface ScopeOverviewProps {
    overview: IScopeOverview | undefined;
    isLoading: boolean;
    onViewDetails: (topicId: number) => void;
}

export const ScopeOverview = ({
    overview,
    isLoading,
    onViewDetails,
}: ScopeOverviewProps) => {
    // Sort topics: those with gaps first (by gap count desc), then alphabetically
    const sortedTopics = useMemo(() => {
        if (!overview?.topics) return [];

        return [...overview.topics].sort((a, b) => {
            const gapDiff = b.gaps.length - a.gaps.length;
            if (gapDiff !== 0) return gapDiff;
            return a.name.localeCompare(b.name);
        });
    }, [overview?.topics]);

    if (isLoading) {
        return (
            <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
                <Box sx={{ p: 3 }}>
                    {/* Skeleton for header */}
                    <Box sx={{ mb: 4 }}>
                        {/* AI Summary skeleton */}
                        <Box
                            sx={{
                                pl: 2.5,
                                py: 1.5,
                                mb: 3,
                                borderLeft: "4px solid",
                                borderColor: "grey.300",
                            }}
                        >
                            <Skeleton
                                variant="text"
                                width={100}
                                height={20}
                                sx={{ mb: 1 }}
                            />
                            <Skeleton variant="text" width="60%" height={24} />
                            <Skeleton variant="text" width="45%" height={24} />
                        </Box>
                        {/* Stats skeleton */}
                        <Skeleton
                            variant="text"
                            width={180}
                            height={32}
                            sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <Skeleton
                                variant="rounded"
                                width={180}
                                height={32}
                                sx={{ borderRadius: 2 }}
                            />
                            <Skeleton
                                variant="rounded"
                                width={140}
                                height={32}
                                sx={{ borderRadius: 2 }}
                            />
                        </Box>
                    </Box>

                    {/* Skeleton for topic cards */}
                    <Grid container spacing={2}>
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={i}>
                                <Box
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: "grey.300",
                                        bgcolor: "background.paper",
                                    }}
                                >
                                    <Skeleton
                                        variant="text"
                                        width="70%"
                                        height={24}
                                        sx={{ mb: 1 }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        width="100%"
                                        height={18}
                                    />
                                    <Skeleton
                                        variant="text"
                                        width="85%"
                                        height={18}
                                        sx={{ mb: 2 }}
                                    />
                                    <Skeleton
                                        variant="rounded"
                                        width="100%"
                                        height={100}
                                        sx={{ borderRadius: 2, mb: 2 }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        width={120}
                                        height={20}
                                    />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>
        );
    }

    if (!overview || overview.topics.length === 0) {
        return (
            <Box p={4}>
                <Typography color="text.secondary" textAlign="center">
                    No topics available for this scope.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                overflow: "auto",
            }}
        >
            <Box sx={{ p: 3 }}>
                <DocumentSummaryHeader
                    headline={overview.headline}
                    topics={overview.topics}
                    showGaps={overview.show_gaps}
                    clausesAnalyzed={overview.clauses_analyzed}
                    clausesFound={overview.clauses_found}
                />

                <Grid container spacing={2}>
                    {sortedTopics.map((topic) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={topic.id}>
                            <TopicCard
                                topic={topic}
                                onViewDetails={() => onViewDetails(topic.id)}
                                showGaps={overview.show_gaps}
                            />
                        </Grid>
                    ))}
                </Grid>

                <LegalDisclaimer />
            </Box>
        </Box>
    );
};
